import type { CustomerCardSpdState, ConsumerSpdEntry, CustomerSpdPresence, CustomerSpdSaveItem } from '../types/customerSpd.types';
import type { CustomerCardSeedPayload } from '../types/CustomerCardTypes';
import type { CustomerSpdAction } from './customerSpdActionCreators';
import type { CustomerCardAction } from './actionCreators';
import { toPresenceFlags, emptyPresence, SPD_FIELDS } from '../components/sections/spd/spd.utils';
import type { SEED_CUSTOMER_CARD } from './actions';
import { SAVE_PARTICIPANTS_SUCCESS } from './actions';
import { BACK, CANCEL_CARD_OPERATION } from './cardOperationActions';
import {
    CHECK_CUSTOMER_SPD_REQUEST,
    CHECK_CUSTOMER_SPD_SUCCESS,
    CHECK_CUSTOMER_SPD_FAILURE,
    GET_CUSTOMER_SPD_REQUEST,
    GET_CUSTOMER_SPD_SUCCESS,
    GET_CUSTOMER_SPD_FAILURE,
    SAVE_CUSTOMER_SPD_REQUEST,
    SAVE_CUSTOMER_SPD_SUCCESS,
    SAVE_CUSTOMER_SPD_FAILURE,
    RESET_CUSTOMER_SPD_LOAD_STATE
} from './customerSpd.actions';

const INITIAL_STATE: CustomerCardSpdState = {
    checkLoading: false,
    consumers: {},
    canDesk: false
};

/** Update a single participant's SPD entry, merging its `status` sub-object. One place per participant. */
const patchConsumer = (state: CustomerCardSpdState, ccid: string, patch: Partial<ConsumerSpdEntry>): CustomerCardSpdState => {
    const prev = state.consumers[ccid];
    const status = { ...prev?.status, ...patch.status };
    return {
        ...state,
        consumers: {
            ...state.consumers,
            [ccid]: {
                ...prev,
                ...patch,
                status
            }
        }
    };
};

const commitSavedSpdPresence = (
    state: CustomerCardSpdState,
    requestedItems: CustomerSpdSaveItem[],
    savedCcids: string[] = requestedItems.map(({ commonConsumerId }) => commonConsumerId)
): CustomerCardSpdState => {
    const requestedByCcid = new Map(requestedItems.map((item) => [item.commonConsumerId, item]));
    return savedCcids.reduce((acc, ccid) => {
        const item = requestedByCcid.get(ccid);
        if (!item) return acc;
        const savedPresence = toPresenceFlags(item);
        const mergedPresence: CustomerSpdPresence = {
            ...emptyPresence(),
            ...acc.consumers[ccid]?.presence
        };

        for (const field of SPD_FIELDS) {
            if (field in item) mergedPresence[field] = savedPresence[field] as never;
        }
        return patchConsumer(acc, ccid, { presence: mergedPresence });
    }, state);
};

/** bankAccountNumber saves with the participants, not with SPD — commit its presence separately. */
const commitBankAccountNumberPresence = (
    state: CustomerCardSpdState,
    item: { commonConsumerId: string; bankAccountNumber?: string }
): CustomerCardSpdState => {
    const mergedPresence: CustomerSpdPresence = {
        ...emptyPresence(),
        ...state.consumers[item.commonConsumerId]?.presence,
        bankAccountNumber: Boolean(item.bankAccountNumber)
    };
    return patchConsumer(state, item.commonConsumerId, { presence: mergedPresence });
};

/**
 * The SPD slice also listens to two of the main slice's actions: the seed action to pick up
 * host-supplied presence flags, and SAVE_PARTICIPANTS_SUCCESS to commit bankAccountNumber presence
 * (that field saves via the participants endpoint — see saveParticipantsRSAA — not
 * SAVE_CUSTOMER_SPD_SUCCESS) and, under the consolidated save, SPD presence as well.
 */
type CustomerSpdReducerAction =
    | CustomerSpdAction
    | { type: typeof SEED_CUSTOMER_CARD; payload?: CustomerCardSeedPayload }
    | { type: typeof BACK | typeof CANCEL_CARD_OPERATION }
    | Extract<CustomerCardAction, { type: typeof SAVE_PARTICIPANTS_SUCCESS }>;

export type CustomerSpdReducer = (
    state: CustomerCardSpdState | undefined,
    action: CustomerSpdReducerAction
) => CustomerCardSpdState;

export const customerSpdReducer: CustomerSpdReducer = (
    state: CustomerCardSpdState = INITIAL_STATE,
    action: CustomerSpdReducerAction
): CustomerCardSpdState => {
    switch (action.type) {
        // Leaving an operation returns the form's SPD to its baseline, so the per-CCID fetch gate has to
        // reopen with it — otherwise Show Data short-circuits on a stale 'success' and reveals nothing.
        // Both exits count: `BACK` from the search step ends an operation just as cancelling does.
        case BACK:
        case CANCEL_CARD_OPERATION:
        case RESET_CUSTOMER_SPD_LOAD_STATE: {
            const resetConsumers = Object.fromEntries(
                Object.entries(state.consumers).map(([ccid, entry]) => [
                    ccid,
                    { ...entry, status: { ...entry.status, getSpdStatus: null } }
                ])
            );
            return { ...state, checkLoading: false, consumers: resetConsumers };
        }

        case CHECK_CUSTOMER_SPD_REQUEST: {
            return { ...state, checkLoading: true };
        }
        case CHECK_CUSTOMER_SPD_SUCCESS: {
            const payload = action.payload;
            // Backend returns { ok: false } when DealXG has no SPD (404) — default all fields to false.
            if (!payload || (payload as { ok?: boolean }).ok === false) {
                const ccids = action.meta?.commonConsumerIds ?? [];
                return ccids.reduce((acc, ccid) => patchConsumer(acc, ccid, { presence: emptyPresence() }), {
                    ...state,
                    checkLoading: false
                } as CustomerCardSpdState);
            }
            // canDesk is a flat sibling of the CCID keys (not per-participant data) — pull it out before
            // treating the rest of the payload as the CCID -> presence map.
            const { canDesk, ...presenceMap } = payload as { canDesk?: boolean } & Record<string, CustomerSpdPresence>;
            return Object.keys(presenceMap).reduce<CustomerCardSpdState>(
                (acc, ccid) => patchConsumer(acc, ccid, { presence: presenceMap[ccid] }),
                {
                    ...state,
                    canDesk: Boolean(canDesk),
                    checkLoading: false
                }
            );
        }
        case CHECK_CUSTOMER_SPD_FAILURE: {
            const ccids = action.meta?.commonConsumerIds ?? [];
            return ccids.reduce((acc, ccid) => patchConsumer(acc, ccid, { status: { checkError: action.payload } }), {
                ...state,
                checkLoading: false
            } as CustomerCardSpdState);
        }

        // GET request — mark this participant's status 'inFlight' in Redux (not component state) so a
        // second call site (e.g. BankInformation's eye icon while CustomerSpd's Show Data is already
        // fetching the same ccid) can see the in-flight request and skip firing a duplicate GET.
        case GET_CUSTOMER_SPD_REQUEST: {
            const ccid = action.meta?.commonConsumerId;
            if (!ccid) return state;
            return patchConsumer(state, ccid, { status: { getSpdStatus: 'inFlight' } });
        }

        // GET success — mark this participant 'success' so subsequent reveal attempts skip the API call.
        case GET_CUSTOMER_SPD_SUCCESS: {
            const ccid = action.meta?.commonConsumerId;
            if (!ccid) return state;
            return patchConsumer(state, ccid, { status: { getSpdStatus: 'success' } });
        }

        // GET failure — PII values are read off the dispatch promise into component state. Status is set
        // to 'error' (still retryable — see useGetSpd's fetchSpd gate).
        case GET_CUSTOMER_SPD_FAILURE: {
            const ccid = action.meta?.commonConsumerId;
            return ccid ? patchConsumer(state, ccid, { status: { getSpdStatus: 'error' } }) : state;
        }
        case SAVE_CUSTOMER_SPD_REQUEST:
            return { ...state, saveError: undefined };
        case SAVE_CUSTOMER_SPD_SUCCESS: {
            // Only commit presence for participants the BFF response reports as saved.
            // Presence values come from the request items (meta), keyed by the saved CCID.
            const savedParticipants = Array.isArray(action.payload) ? action.payload : [];
            return commitSavedSpdPresence(
                state,
                action.meta?.items ?? [],
                savedParticipants.map(({ ccid }) => ccid)
            );
        }
        case SAVE_CUSTOMER_SPD_FAILURE:
            return { ...state, saveError: action.payload };

        // Commit bankAccountNumber (saves via participants endpoint) and SPD presence (consolidated save only).
        case SAVE_PARTICIPANTS_SUCCESS: {
            if ((action.payload as { ok?: boolean } | null)?.ok === false) return state;
            const items = action.meta?.spdItems ?? [];
            const withSpdPresence = commitSavedSpdPresence(state, items);
            const bankAccountNumber = action.meta?.bankAccountNumberByCcid;
            return bankAccountNumber ? commitBankAccountNumberPresence(withSpdPresence, bankAccountNumber) : withSpdPresence;
        }

        default:
            return state;
    }
};
